import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { Dialog } from '@headlessui/react';

const LoginPage = ({ setIsAuthenticated, setUserRole }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Login only
      const response = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });
      
      if (response.data.status === 'success') {
        const user = response.data.data.user;
        const userRole = user.role || 'customer';
        
        console.log('Login response:', response.data);
        console.log('User data:', user);
        console.log('User role:', userRole);
        
        // Store token and user info in localStorage
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Update authentication state and user role
        setIsAuthenticated(true);
        if (setUserRole) {
          setUserRole(userRole);
        }
        
        // Redirect based on role
        if (userRole === 'admin') {
          console.log('Redirecting to admin panel...');
          navigate('/admin');
        } else {
          console.log('Redirecting to customer dashboard...');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(
        error.response?.data?.message || 
        'Login failed. Please check your credentials and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetSuccess('');
    setResetError('');
    try {
      const response = await api.post('/auth/request-password-reset', { email: resetEmail });
      if (response.data.status === 'success') {
        setResetSuccess('If an account exists for this email, a reset link will be sent. Please check your inbox.');
        setResetEmail('');
      } else {
        setResetError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setResetError('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-palamuru-blue via-palamuru-blue/90 to-palamuru-slate flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Brand */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-palamuru-saffron to-palamuru-gold rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-strong">
            <span className="text-white font-display-black text-3xl">P</span>
          </div>
          <h1 className="font-display-bold text-4xl text-white mb-2">
            Palamuru Constructions
          </h1>
          <p className="font-elegant text-xl text-white/90">
            Building Dreams, Delivering Excellence
          </p>
        </div>

        {/* Login/Register Form */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-strong border border-white/20 p-8">
          <div className="text-center mb-8">
            <h2 className="font-display-bold text-2xl text-gray-800 mb-2">
              Welcome Back
            </h2>
            <p className="font-body text-gray-600">
              Sign in to access your construction dashboard
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-600 font-body text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <p className="text-green-600 font-body text-sm">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block font-body-semibold text-sm text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-palamuru-blue/50 focus:border-palamuru-blue transition-all duration-200 font-body"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block font-body-semibold text-sm text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-palamuru-blue/50 focus:border-palamuru-blue transition-all duration-200 font-body"
                placeholder="Enter your password"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-palamuru-blue border-gray-300 rounded focus:ring-palamuru-blue/50"
                />
                <span className="ml-2 font-body text-sm text-gray-600">Remember me</span>
              </label>
              <button
                type="button"
                className="font-body-semibold text-sm text-palamuru-blue hover:text-palamuru-saffron transition-colors"
                onClick={() => setShowResetModal(true)}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-palamuru-saffron to-palamuru-gold text-white font-body-bold py-3 px-6 rounded-xl shadow-soft hover:shadow-medium transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="font-body text-sm text-gray-500">
              Need access? Contact your administrator for an invite.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="font-elegant text-white/70 text-sm">
            © 2024 Palamuru Constructions. All rights reserved.
          </p>
        </div>

        {/* Password Reset Modal */}
        <Dialog open={showResetModal} onClose={() => { setShowResetModal(false); setResetEmail(''); setResetSuccess(''); setResetError(''); }}>
          <div className="fixed inset-0 bg-black bg-opacity-40" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
            <Dialog.Panel className="relative bg-white rounded-2xl shadow-strong border border-white/20 w-full max-w-md mx-auto p-8 z-10">
              <Dialog.Title className="text-2xl font-display-bold text-gray-900 mb-2 text-center">Reset Your Password</Dialog.Title>
              <p className="text-gray-600 text-center mb-6">Enter your email address and we'll send you a link to reset your password.</p>
              {resetSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700 text-center mb-4">{resetSuccess}</div>
              ) : (
                <form onSubmit={handleResetRequest} className="space-y-4">
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-palamuru-blue/50 focus:border-palamuru-blue font-body"
                    placeholder="Enter your email"
                  />
                  {resetError && <div className="text-red-600 text-sm font-body-medium text-center">{resetError}</div>}
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-palamuru-saffron to-palamuru-gold text-white font-body-bold py-3 px-6 rounded-xl shadow-soft hover:shadow-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={resetLoading}
                  >
                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              )}
              <button
                type="button"
                className="mt-6 w-full text-center font-body-semibold text-palamuru-blue hover:text-palamuru-saffron transition-colors"
                onClick={() => { setShowResetModal(false); setResetEmail(''); setResetSuccess(''); setResetError(''); }}
              >
                Cancel
              </button>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default LoginPage; 